import classNames from 'classnames';
import { Portal, svgSprite } from '../../../../../../hybrid/wrappers';
import css from './SelectProductsModal.module.scss';
import T from '../../../../../../common/components/T';
import FormRadio from '../../../form-radio/FormRadioSwitcher/FormRadioSwitcher';
import { useContext, useEffect, useState } from 'react';
import Skeleton from '../../../../../plug/Skeleton/Skeleton';
import { adminGridSkeleton } from '../../../../../plug/Skeleton/components/patterns';
import GridFilter from '../../../../../gridFilter/GridFilter/GridFilter';
import UserContext from '../../../../../../common/utils/getContext';
import { getData } from '../../../../../../common/utils/router';
import { thead, transformData } from './helpers';
import { LangContext } from '../../../../../../common/decorators/Language';
import SingleButton from '../../../../form-control-buttons/SingleButton/SingleButton';
import { generateUniqueId } from '../../../../../../common/utils/utils';

const SelectProductsModal = ({ closeHandler, value = [], setFormData, attribute, isOnlyProducts = false }) => {
    const { translation } = useContext(LangContext);
    const { userToken, lang, setAlertData } = useContext(UserContext);
    const [selectedType, setSelectedType] = useState(isOnlyProducts ? 1 : 0);
    const [products, setProducts] = useState(null);
    const [sets, setSets] = useState(null);

    const [transformedProducts, setTransformedProducts] = useState(null);
    const [transformedSets, setTransformedSets] = useState(null);

    const [val, setVal] = useState(value);

    useEffect(() => {
        setVal(value);
    }, [value]);

    useEffect(() => {
        if (sets) {
            setTransformedSets({
                ...sets,
                tbody: {
                    ...(sets?.tbody || {}),
                    data: sets?.tbody?.data?.map((set) => {
                        const inValue = val?.find((it) => +it.id === +set.id);
                        if (inValue) {
                            return {
                                ...set,
                                activeRow: true,
                                quantity: inValue?.quantity
                            };
                        }
                        return set;
                    })
                }
            });
        }
    }, [sets, val]);

    useEffect(() => {
        if (products) {
            setTransformedProducts({
                ...products,
                tbody: {
                    ...(products?.tbody || {}),
                    data: products?.tbody?.data?.map((product) => {
                        const inValue = val?.find((it) => +it.id === +product.id);
                        if (inValue) {
                            return {
                                ...product,
                                activeRow: true,
                                quantity: inValue?.quantity
                            };
                        }
                        return product;
                    })
                }
            });
        }
    }, [products, val]);

    useEffect(() => {
        getData(
            '/api/v1/shop/products?page[size]=999&filter[product_availability]=in_stock,expected_arrival',
            userToken,
            lang
        ).then((res) => {
            if (!res?.data) {
                closeHandler();
                return setAlertData({
                    type: 'error',
                    cont: res?.message || <T textName='error-products-load' defaultStr='Ошибка загрузки продуктов' />
                });
            }
            setSets({
                thead,
                tbody: transformData(translation, 0)(res)
            });
            setProducts({
                thead,
                tbody: transformData(translation, 1)(res)
            });
        });
    }, [userToken, lang, translation, setAlertData]);

    const inputHandler = (row, value) => {
        if (!row?.activeRow && +value > 1) {
            setVal((s) => [...s, { ...row, activeRow: true, quantity: +value }]);
        }
        if (row?.activeRow) {
            if (+value <= 0) {
                setVal((s) => s?.filter((it) => +it?.id !== +row?.id));
            } else {
                setVal((s) =>
                    s?.map((item) => {
                        if (+item.id === +row.id) {
                            return {
                                ...item,
                                activeRow: +row?.quantity === 1 ? false : true,
                                quantity: +value
                            };
                        }
                        return item;
                    })
                );
            }
        }
    };

    const quantityHandler = (row, type) => {
        if (!row?.activeRow && type === 'plus') {
            setVal((s) => [...s, { ...row, activeRow: true, quantity: 2 }]);
        }
        if (row?.activeRow) {
            if (+row?.quantity === 1 && type === 'minus') {
                setVal((s) => s?.filter((it) => +it?.id !== +row?.id));
            } else {
                setVal((s) =>
                    s?.map((item) => {
                        if (+item.id === +row.id) {
                            return {
                                ...item,
                                activeRow: +row?.quantity === 1 ? false : true,
                                quantity: type === 'plus' ? +row?.quantity + 1 : +row?.quantity > 1 ? +row?.quantity - 1 : 1
                            };
                        }
                        return item;
                    })
                );
            }
        }
    };

    const activeRowHandler = (row) => {
        if (row?.activeRow) {
            setVal((s) => s?.filter((it) => +it?.id !== +row?.id));
        } else {
            setVal((s) => [...s, row]);
        }
    };

    return (
        <Portal closeHandler={closeHandler}>
            <div className={css.modal}>
                <div onClick={closeHandler} className={classNames('circleButton', css.modalClose)}>
                    {svgSprite('cross')}
                </div>
                <p className={css.modalTitle}>
                    <T textName='selecting_a_product_for_the_invoice' defaultStr='Выбор товара для накладной' />
                </p>
                {isOnlyProducts ? null : (
                    <FormRadio
                        options={[
                            <T key={generateUniqueId()} textName='package' defaultStr='Пакет' />,
                            <T key={generateUniqueId()} textName='goods' defaultStr='Товар' />
                        ]}
                        value={selectedType}
                        setFormData={(attr, value) => setSelectedType(value)}
                        compStyle={{
                            content: css.formRadio
                        }}
                    />
                )}
                <Skeleton
                    wait={!!transformedSets}
                    pattern={adminGridSkeleton}
                    Component={GridFilter}
                    componentProps={{
                        grid: {
                            initData: selectedType === 0 ? transformedSets : transformedProducts,
                            setInitData: selectedType === 0 ? setSets : setProducts,
                            localUpdate: true,
                            gridStyles: {
                                gridWrapper: {
                                    className: css.gridWrapper
                                },
                                activeRow: {
                                    td: {
                                        className: css.activeRow
                                    }
                                }
                            },
                            handlers: {
                                quantity: {
                                    plus: {
                                        onClick: (row) => quantityHandler(row, 'plus')
                                    },
                                    minus: {
                                        onClick: (row) => quantityHandler(row, 'minus')
                                    },
                                    change: {
                                        onChange: (row, setGridData, v, e) => {
                                            const val = e.target.value;
                                            inputHandler(row, val);
                                            // ставимо ширину поля від ширини контента
                                            const newInputWidth = String(val)?.length * 1.4 + 'ch';
                                            if (e && e.target) {
                                                e.target.style.width = newInputWidth;
                                            }
                                        }
                                    }
                                },
                                activeRow: {
                                    change: {
                                        onChange: activeRowHandler
                                    }
                                }
                            }
                        },
                        filter: {
                            url: '/api/v1/shop/products?filter[product_availability]=in_stock,expected_arrival',
                            data: () => []
                        },
                        textsData: {},
                        convertData: transformData(translation, selectedType),
                        customStyles: css,
                        showBy: {
                            options: [999],
                            value: 0
                        }
                    }}
                />
                <SingleButton
                    type='ok'
                    buttonText={<T textName='confirm' defaultStr='Подтвердить' />}
                    clickFunc={() => {
                        setFormData(attribute, val);
                        closeHandler();
                    }}
                    customButton={{
                        singleButton: css.applyButtonWrapper
                    }}
                />
            </div>
        </Portal>
    );
};

export default SelectProductsModal;
